import { gql, useMutation } from '@apollo/client';
import * as React from 'react';

import type { UpdateCalloutMutation, UpdateCalloutMutationVariables } from './__generated__/useUpdateCallout.gql';

const UPDATE_CALLOUT = gql`
  mutation UpdateCallout($id: ID!, $input: CalloutUpdateInputGQL!) {
    updateCallout(id: $id, input: $input) {
      id
      dismissed
      feedbackRating
      feedbackNotes
      transaction {
        id
      }
    }
  }
`;

interface IDismissCalloutOptions {
  calloutId: string;
  /** Thumbs-up/down signal. Mapped to `feedbackRating: 5/1` at the mutation boundary. */
  appropriate?: boolean;
}

/**
 * Maps the legacy thumbs-up/down boolean onto the new 1-5 feedback column.
 * The 5-point picker UI lands in a follow-up; this keeps the existing
 * affordance writing real signal in the meantime.
 *
 * Only the positive (helpful → 5) side is forwarded. The current dismiss-
 * confirmation prompt asks something like "Was this helpful?" with a yes/no
 * answer, so a "no" can equally mean "not helpful" (≈1) or "neutral / I
 * didn't really feel either way" (≈3). Until the 5-point picker lands and
 * the user can express that distinction explicitly, we conservatively
 * discard the negative branch rather than write a falsely confident `1`.
 *
 * Exported for unit testing.
 */
export const mapAppropriateToFeedbackRating = (appropriate: boolean | undefined): number | undefined => {
  if (appropriate === true) {
    return 5;
  }
  return undefined;
};

/**
 * Hook for updating callouts.
 * Provides a dismissCallout helper for convenience.
 *
 * Note: All cache updates for this mutation are handled globally in ApolloProvider.tsx
 */
export const useUpdateCallout = () => {
  const [updateCallout, result] = useMutation<UpdateCalloutMutation, UpdateCalloutMutationVariables>(UPDATE_CALLOUT);

  const dismissCallout = React.useCallback(
    async ({ calloutId, appropriate }: IDismissCalloutOptions) => {
      const feedbackRating = mapAppropriateToFeedbackRating(appropriate);
      await updateCallout({
        variables: {
          id: calloutId,
          input: {
            dismissed: true,
            ...(feedbackRating !== undefined && { feedbackRating }),
          },
        },
      });
    },
    [updateCallout]
  );

  return { updateCallout, dismissCallout, ...result };
};
